import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadMemoryList,
  loadMemories,
  modifyMemory,
  removeMemory,
  saveMemory,
} from '../api/memory'
import {
  CreateMemory,
  Memory,
  MemoryFilters,
  MemoryListRequest,
  PaginatedMemoriesResponse,
  PaginationMeta,
} from '../types/memory'

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

// get memories
export const getMemories = createAsyncThunk(
  'memory/getMemory',
  async (_, thunkApi) => {
    try {
      return await loadMemories()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

export const getMemoryList = createAsyncThunk(
  'memory/getMemoryList',
  async (request: MemoryListRequest, thunkApi) => {
    try {
      return await loadMemoryList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// create memory
export const addMemory = createAsyncThunk(
  'memory/addMemory',
  async (memory: CreateMemory, thunkApi) => {
    try {
      return await saveMemory(memory)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// update memory
export const updateMemory = createAsyncThunk(
  'memory/updateMemory',
  async (memory: Memory, thunkApi) => {
    try {
      return await modifyMemory(memory)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// delete memory
export const deleteMemory = createAsyncThunk(
  'memory/deleteMemory',
  async (id: number, thunkApi) => {
    try {
      return await removeMemory(id)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface MemorySliceState {
  memories: Memory[]
  pagedMemories: Memory[]
  memoriesLoading: boolean
  memoriesError?: SerializedError
  filters: MemoryFilters
  pagination: PaginationMeta
  activeListRequestId?: string
}

const initialState: MemorySliceState = {
  memories: [],
  pagedMemories: [],
  memoriesLoading: false,
  memoriesError: undefined,
  filters: {
    name: '',
    tag: '',
  },
  pagination: emptyPagination,
  activeListRequestId: undefined,
}

const memorySlice = createSlice({
  name: 'memories',
  initialState,
  reducers: {
    setMemoryFilters: (
      state: MemorySliceState,
      action: PayloadAction<MemoryFilters>,
    ) => {
      state.filters = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMemories.fulfilled, (state, action: PayloadAction<Memory[]>) => {
      state.memories = action.payload
      state.memoriesLoading = false
      state.memoriesError = undefined
    })
    builder.addCase(getMemories.pending, (state) => {
      state.memoriesLoading = true
      state.memoriesError = undefined
    })
    builder.addCase(getMemories.rejected, (state, action) => {
      state.memoriesLoading = false
      state.memoriesError = action.error
    })
    builder.addCase(getMemoryList.fulfilled, (
      state,
      action: PayloadAction<PaginatedMemoriesResponse> & { meta: any },
    ) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedMemories = action.payload.items
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.activeListRequestId = undefined
      state.memoriesLoading = false
      state.memoriesError = undefined
    })
    builder.addCase(getMemoryList.pending, (state, action: any) => {
      state.activeListRequestId = action.meta.requestId
      state.pagedMemories = []
      state.memoriesLoading = true
      state.memoriesError = undefined
    })
    builder.addCase(getMemoryList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.memoriesLoading = false
      state.memoriesError = action.error
    })
    builder.addCase(addMemory.fulfilled, (state, action: PayloadAction<Memory>) => {
      state.memories.unshift(action.payload)

      const matchesFilters =
        (!state.filters.name ||
          action.payload.name
            .toLocaleLowerCase()
            .includes(state.filters.name.toLocaleLowerCase())) &&
        (!state.filters.tag ||
          action.payload.tags.some((tag) =>
            tag.toLowerCase().includes(state.filters.tag.toLocaleLowerCase()),
          ))

      if (matchesFilters && state.pagination.page === 1) {
        state.pagedMemories = [action.payload, ...state.pagedMemories].slice(
          0,
          state.pagination.limit,
        )
        state.pagination.total += 1
        state.pagination.totalPages = Math.ceil(
          state.pagination.total / state.pagination.limit,
        )
      }

      state.memoriesLoading = false
      state.memoriesError = undefined
    })
    builder.addCase(addMemory.pending, (state) => {
      state.memoriesLoading = true
      state.memoriesError = undefined
    })
    builder.addCase(addMemory.rejected, (state, action) => {
      state.memoriesLoading = false
      state.memoriesError = action.error
    })
    builder.addCase(updateMemory.fulfilled, (state, action: PayloadAction<Memory>) => {
      state.memories = state.memories.map((memory) =>
        memory.id === action.payload.id ? action.payload : memory,
      )
      state.pagedMemories = state.pagedMemories.map((memory) =>
        memory.id === action.payload.id ? action.payload : memory,
      )
      state.memoriesLoading = false
      state.memoriesError = undefined
    })
    builder.addCase(updateMemory.pending, (state) => {
      state.memoriesLoading = true
      state.memoriesError = undefined
    })
    builder.addCase(updateMemory.rejected, (state, action) => {
      state.memoriesLoading = false
      state.memoriesError = action.error
    })
    builder.addCase(deleteMemory.fulfilled, (state, action: PayloadAction<number>) => {
      const wasOnCurrentPage = state.pagedMemories.some(
        (memory) => memory.id === action.payload,
      )

      state.memories = state.memories.filter(
        (note) => note.id !== action.payload,
      )

      state.pagedMemories = state.pagedMemories.filter(
        (note) => note.id !== action.payload,
      )

      if (wasOnCurrentPage && state.pagination.total > 0) {
        state.pagination.total -= 1
        state.pagination.totalPages =
          state.pagination.total === 0
            ? 0
            : Math.ceil(state.pagination.total / state.pagination.limit)
      }

      state.memoriesLoading = false
      state.memoriesError = undefined
    })
    builder.addCase(deleteMemory.pending, (state) => {
      state.memoriesLoading = true
      state.memoriesError = undefined
    })
    builder.addCase(deleteMemory.rejected, (state, action) => {
      state.memoriesLoading = false
      state.memoriesError = action.error
    })
  },
})

export const { setMemoryFilters } = memorySlice.actions
export default memorySlice.reducer
